import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import "../css/style.css";
import "../css/zerogrid.css";
import "../css/menu.css";
import "../css/modern-dashboard.css";
import "./UseTimeDetail.css";
import api from '../api';

// Helper functions
const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatCurrency = (value) => {
  return `₩${numberWithCommas(value.toFixed(0))}`;
};

const convertMinutesToTimeFormat = (minutes) => {
  if (minutes < 0) return "invalid";
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = remainingMinutes.toString().padStart(2, "0");
  return formattedHours + ":" + formattedMinutes;
};

// Commission Table Component
const CommissionTable = ({ data, title }) => {
  // Add defensive programming to handle missing data
  if (!data || !data.deviceNames || !data.tableData || data.deviceNames.length === 0) {
    return (
      <div className="commission-table-section">
        <h2>{title}</h2>
        <div className="table-responsive">
          <div className="text-center p-4">
            <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
            <p className="text-muted">해당 기간에 데이터가 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  const { tableData, deviceNames, workingDays, feeRate, year, month, daysInMonth } = data;
  
  // Sort device names using the same logic as ShowMeTheMoney page
  const sortedDeviceNames = [...deviceNames].sort((a, b) => {
    // 알파벳과 숫자를 분리 (예: "M2-3" -> ["M2-", 3])
    const keyA = a.replace(/[^0-9\-]/g, '');
    const keyB = b.replace(/[^0-9\-]/g, '');
    const aParts = keyA.split('-').map(Number);
    const bParts = keyB.split('-').map(Number);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] ?? 0;
      const bVal = bParts[i] ?? 0;

      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
    }

    return 0; // 동일하면 그대로
  });
  
  // Calculate totals
  const deviceTotals = sortedDeviceNames.map(deviceName => {
    const columnData = Object.values(tableData).map(rowData => rowData[deviceName] || 0);
    return columnData.reduce((sum, value) => sum + value, 0);
  });
  
  const grandTotal = Object.values(tableData).flatMap(rowData => Object.values(rowData)).reduce((sum, value) => sum + value, 0);
  
  return (
    <div className="commission-table-section">
      <h2>{title}</h2>
      <div className="table-responsive">
        <table className="table table-striped">
          <thead className="thead-dark">
            <tr>
              <th>날짜</th>
              {sortedDeviceNames.map(deviceName => (
                <th key={deviceName}>{deviceName}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(tableData).map(date => {
              const rowData = tableData[date];
              const total = sortedDeviceNames.reduce((sum, deviceName) => sum + (rowData[deviceName] || 0), 0);
              
              return (
                <tr key={date}>
                  <td className="first-column">
                    {date}
                    <span className="currency-amount">
                      {formatCurrency((total / 60) * feeRate)}
                    </span>
                  </td>
                  {sortedDeviceNames.map(deviceName => (
                    <td key={deviceName}>
                      {rowData[deviceName] ? (
                        <>
                          {convertMinutesToTimeFormat(rowData[deviceName])} h
                          <span className="currency-amount">
                            ({formatCurrency(rowData[deviceName]/60 * feeRate)})
                          </span>
                        </>
                      ) : ''}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>총합</td>
              {sortedDeviceNames.map((deviceName, index) => (
                <td key={deviceName}>
                  {convertMinutesToTimeFormat(deviceTotals[index])} h
                  <span className="currency-amount">
                    ({formatCurrency((deviceTotals[index] / 60) * feeRate)})
                  </span>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      
      {/* 총합 및 출석률 카드 */}
      <div className="commission-summary-cards">
        <div className="summary-card total-summary">
          <div className="summary-header">
            <i className="fas fa-calculator"></i>
            <h3>전체 총합</h3>
          </div>
          <div className="summary-content">
            <div className="total-hours">
              {(grandTotal / 60).toFixed(2)} 시간
            </div>
            <div className="total-amount">
              {formatCurrency(((grandTotal / 60) * feeRate))}
            </div>
            <div className="hourly-rate">
              시간당 {formatCurrency(feeRate)}원
            </div>
          </div>
        </div>
        
        <div className="summary-card attendance-summary">
          <div className="summary-header">
            <i className="fas fa-calendar-check"></i>
            <h3>{month}월 출석률</h3>
          </div>
          <div className="summary-content">
            <div className="attendance-ratio">
              {((workingDays/daysInMonth) * 100).toFixed(2)}%
            </div>
            <div className="attendance-days">
              {workingDays}일 / {daysInMonth}일
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function UseTimeDetail({host, changePage}) {
  const [apiKey, setApiKey] = useState("");
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);

  useEffect(() => {
    api.get("/getInvoiceList").then((res) => {
      const formatted = res.data.map((entry) => {
        const matches = entry.match(/usetimes(\d+)-(\d+)/);
        if (matches && matches.length === 3) {
          const year = matches[1];
          const month = matches[2];
          return { value: `${year}-${month}`, label: `${year}년 ${month}월` };
        }
        return null;
      }).filter(Boolean);
      setInvoiceList(formatted);
      
      // 첫 번째 항목을 기본값으로 설정
      if (formatted.length > 0) {
        setSelectedMonth(formatted[0].value);
      }
    });
  }, []);

  const handleSubmit = () => {
    if (!apiKey || !selectedMonth) return alert("암호키와 명세서를 선택하세요");
    setIsModalOpen(false);
    setLoading(true);
    api
      .get(`/useTimeDetailAPI?hostName=${host}&apiKey=${apiKey}&yearAndMonth=${selectedMonth}`)
      .then((res) => {
        setCommissionData(res.data);
      })
      .catch((err) => {
        alert(err.response?.data || "요청 실패");
      })
      .finally(() => setLoading(false));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="modern-dashboard scrollable-page">
      {loading && (
        <div className="modern-overlay">
          <div className="spinner-container">
            <div className="modern-spinner spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
            </div>
            <p className="loading-text">커미션 명세서를 불러오는 중...</p>
          </div>
        </div>
      )}

            <ReactModal
              isOpen={isModalOpen}
              onRequestClose={() => setIsModalOpen(false)}
              contentLabel="커미션 명세서"
        ariaHideApp={false}
        className="modern-modal-container"
        overlayClassName="modern-modal-overlay"
        shouldCloseOnOverlayClick={false}
            >
        <div className="modern-modal-content">
          <div className="modal-header-section">
            <div className="modal-title-area">
              <h2 className="modal-title">
                <i className="fas fa-file-invoice-dollar me-2"></i>
                커미션 명세서
              </h2>
              <p className="modal-subtitle">{host}님의 수익 명세를 확인하세요</p>
            </div>
            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
              </div>
          
          <div className="modal-body-section">
            <div className="modern-form-group mb-4">
              <label className="modern-label">
                <i className="fas fa-key me-2"></i>
                암호키
              </label>
                  <input
                    type="password"
                className="modern-input"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="암호키를 입력하세요"
                  />
                </div>
            
            <div className="modern-form-group">
              <label className="modern-label">
                <i className="fas fa-calendar-alt me-2"></i>
                명세서 기간
              </label>
                  <select
                className="modern-input"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                onKeyPress={handleKeyPress}
                  >
                <option value="">기간을 선택하세요</option>
                    {invoiceList.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
          
          <div className="modern-modal-footer">
            <button 
              type="button" 
              className="modern-button" 
              onClick={handleSubmit}
              disabled={!apiKey || !selectedMonth}
            >
              <i className="fas fa-search me-2"></i>
              명세서 조회
            </button>
          </div>
        </div>
      </ReactModal>

      <section className="modern-container">
        <div className="wrap-container">
          <div className="admin-header">
            <div className="admin-title-section">
              <h1 className="admin-title">
                <i className="fas fa-file-invoice-dollar me-3"></i>
                커미션 명세서
              </h1>
              <p className="admin-subtitle">{host}님의 수익 명세 및 통계</p>
            </div>
            <button 
              className="back-to-main-btn"
              onClick={() => changePage('main')}
            >
              <i className="fas fa-home me-2"></i>
              메인으로 돌아가기
            </button>
          </div>
          
          {!isModalOpen && !loading && commissionData && (
            <>
              <div className="modern-card">
                <div className="card-header">
                  <h4 className="card-title">
                    <i className="fas fa-file-invoice me-2"></i>
                    상세 명세서
                  </h4>
                  <button 
                    className="btn btn-outline-light btn-sm"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <i className="fas fa-edit me-1"></i>
                    다시 조회
                  </button>
                </div>
                <div className="card-body">
                  <div className="commission-content">
                    <CommissionTable data={commissionData.sub} title="부주" />
                    <CommissionTable data={commissionData.main} title="본섭" />
                    <CommissionTable data={commissionData.toy} title="놀자" />
                  </div>
                </div>
              </div>
            </>
          )}
          
          {!isModalOpen && !loading && !commissionData && (
            <div className="modern-card">
              <div className="card-body text-center">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <h3 className="text-muted">명세서를 조회해주세요</h3>
                <p className="text-muted">암호키와 기간을 입력하여 커미션 명세서를 확인하세요</p>
                <button 
                  className="modern-button"
                  onClick={() => setIsModalOpen(true)}
                >
                  <i className="fas fa-plus me-2"></i>
                  명세서 조회하기
                </button>
              </div>
          </div>
          )}
        </div>
      </section>
    </div>
  );
}
